// @flow

import React, { Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { renderToString } from 'react-dom/server';
import {
  geminiColors,
  visioGlobeDefaultColor,
  visioGlobeSelectedColor,
  visioGlobeUnselectedColor,
  reconciliationStates,
  visioGlobeLocomotiveColor,
  visioglobeWhiteTextColor,
} from '../../../../../const';
import { getCurrentShoppingCenterFromStore } from '../../../../../utils/urlUtils';
import { autoQuantityCurrencyString } from '../../../../../utils/formatUtils';
import {
  computeColorZone,
  getAllDisplayableUnits,
  getAllDisplayableUnitsWithBestRetailer,
  initMap,
} from '../../../../mall/map/gemini/geminiFunctions';
import { trackAction } from '../../../../../components/GoogleAnalyticsWrapper';
import IconButton from '../../../../../components/IconButton';
import VisioGlobeUI from '../../../../common/VisioGlobeUI';
import type {
  CoqueRetailer,
  GoodwillType,
} from '../../../../../types/apolloType';
import GoodwillQuantilsLegend from './GoodwillQuantilsLegend';
import type { StoreGemini } from '../../../../../types/geminiType';
import GoodwillVisioglobeTooltip from './GoodwillVisioglobeTooltip';

type Props = {
  mapviewer: MapViewer,
  visioglobeUnitsMap: VisioglobeUnitsMap,
  bestRetailer: CoqueRetailer,
  topRetailerIds: Array<number>,
  selectedRatioName: string,
  goodwill: GoodwillType,
}

type State = {
  selectedStore: ?StoreGemini,
  initialStoreInMap: ?StoreGemini,
  legendHidden: boolean,
}

const STORE_SELECT = 'STORE_SELECT';

class GoodwillOverlay extends React.Component<Props, State> {
  state: State = {
    selectedStore: null,
    initialStoreInMap: null,
    legendHidden: false,
  };

  shoppingCenter = getCurrentShoppingCenterFromStore();

  zoneColorMap = {};

  getSelectedRatio = (goodwill: GoodwillType, selectedRatioName: string) =>
    ((goodwill && goodwill[selectedRatioName]) ? goodwill[selectedRatioName] : null);

  isStoreALocomotive = store =>
    this.props.topRetailerIds
    && this.props.topRetailerIds.includes(store.retailerId);

  componentDidMount() {
    this.props.mapviewer.setSelectStoreCallback(this.selectStoreCallback);

    const currentStoreCodstr = this.props.bestRetailer.codeStoreCoque;
    const displayableUnits = getAllDisplayableUnitsWithBestRetailer(
      this.props.visioglobeUnitsMap,
      currentStoreCodstr,
      this.props.bestRetailer.retailerName,
    );
    this.zoneColorMap = computeColorZone(displayableUnits);

    initMap(
      displayableUnits,
      this.props.mapviewer,
    );

    this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.selectedRatioName,
      this.props.goodwill,
    );

    const initialStoreInMap = displayableUnits.find(store => currentStoreCodstr
      && store.codstr === currentStoreCodstr);

    if (initialStoreInMap) {
      this.setInitialStore(initialStoreInMap);
      this.selectStoreCallback(initialStoreInMap);
      this.props.mapviewer
        .selectPlace(initialStoreInMap.idVisioglobes, () => this.forceUpdate());
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State, nextContext: any): boolean {
    if (this.props.selectedRatioName !== nextProps.selectedRatioName) {
      this.updateMapColor(
        nextProps.visioglobeUnitsMap,
        nextProps.selectedRatioName,
        this.props.goodwill,
      );
    }
    return true;
  }

  formatGoodwillData = (dataToFormat: number) => autoQuantityCurrencyString(dataToFormat, 1);

  findTooltipDataByIdVisioglobes = (visioglobesIds: string[]) =>
    this.props.goodwill.ratiosByCoque
      .filter(coque => coque.visioglobeIds
        .some(visioglobeId => visioglobesIds.includes(visioglobeId)))[0]

  popupHtmlText = (idVisioglobes: string[], storeName: string, isLocomotive: boolean) => {
    const coqueTooltipData = this.findTooltipDataByIdVisioglobes(idVisioglobes);
    return renderToString(<GoodwillVisioglobeTooltip
      coqueData={coqueTooltipData}
      storeName={storeName}
      isLocomotive={isLocomotive}
      format={this.formatGoodwillData}
    />);
  }

  setInitialStore = (initialStoreInMap: StoreGemini) => this.setState({ initialStoreInMap });

  selectStoreCallback = (selectedStore: StoreGemini, visioglobePOI) => {
    if (!selectedStore) {
      return;
    }
    trackAction(STORE_SELECT);

    this.setState({
      selectedStore,
    });
    this.updateMapColor(
      this.props.visioglobeUnitsMap,
      this.props.selectedRatioName,
      this.props.goodwill,
    );

    if (this.props.goodwill && selectedStore !== this.state.initialStoreInMap) {
      this.props.mapviewer.openGoodwillPopup(
        visioglobePOI,
        this.popupHtmlText(
          selectedStore.idVisioglobes,
          selectedStore.storeName,
          this.isStoreALocomotive(selectedStore),
        ),
      );
    }
  };

  resetMapColor = (visioglobeUnitsMap: VisioglobeUnitsMap) => {
    this.props.mapviewer.updateColors(getAllDisplayableUnits(visioglobeUnitsMap).map((store) => {
      const storeColor = {
        storeIds: store.idVisioglobes,
        color: visioGlobeDefaultColor,
        storeName: store.storeName || '',
      };

      if (this.state.selectedStore
        && this.state.selectedStore.idVisioglobes === store.idVisioglobes) {
        storeColor.color = visioGlobeSelectedColor;
      }
      return storeColor;
    }));
  }

  updateMapColor = (
    visioglobeUnitsMap: VisioglobeUnitsMap,
    selectedRatioName: string,
    goodwill: GoodwillType,
  ) => {
    const selectedRatio = this.getSelectedRatio(goodwill, selectedRatioName) || [];
    if (selectedRatio.length === 0) {
      this.resetMapColor(visioglobeUnitsMap);
      return;
    }

    const {
      q1, q2, q3, q4, q5,
    } = selectedRatio.quartilsRatio;

    this.props.mapviewer.updateColors(getAllDisplayableUnits(visioglobeUnitsMap).map((store) => {
      const storeColor = {
        storeIds: store.idVisioglobes,
        color: visioGlobeDefaultColor,
        storeName: store.storeName || '',
      };

      if (this.state.initialStoreInMap
        && this.state.initialStoreInMap.idVisioglobes === store.idVisioglobes) {
        storeColor.color = visioGlobeSelectedColor;
        return storeColor;
      }
      if (this.isStoreALocomotive(store)) {
        storeColor.color = visioGlobeLocomotiveColor;
        storeColor.textColor = visioglobeWhiteTextColor;
        return storeColor;
      }
      if (store.state === reconciliationStates.HISTORY || store.isRental) {
        storeColor.color = visioGlobeUnselectedColor;
        return storeColor;
      }

      const impactedStore = selectedRatio.impactedStores.find(impactedStoreBean =>
        impactedStoreBean.visioglobeIds.some(visioglobeId =>
          store.idVisioglobes.includes(visioglobeId)));

      if (!impactedStore || impactedStore.impactValue === 0) {
        storeColor.color = visioGlobeDefaultColor;
        return storeColor;
      }
      const { impactValue } = impactedStore;
      if (q5 && impactValue > q5) {
        storeColor.color = geminiColors.veryVeryLow;
      } else if (q4 && impactValue > q4) {
        storeColor.color = geminiColors.veryLow;
      } else if (q3 && impactValue > q3) {
        storeColor.color = geminiColors.low;
      } else if (q2 && impactValue > q2) {
        storeColor.color = geminiColors.high;
      } else if (q1 && impactValue > q1) {
        storeColor.color = geminiColors.veryHigh;
      } else {
        storeColor.color = geminiColors.veryVeryHigh;
      }
      return storeColor;
    }));
  };

  hideLegend = () => this.setState({ legendHidden: !this.state.legendHidden });

  render() {
    const selectedRatio = this.getSelectedRatio(this.props.goodwill, this.props.selectedRatioName);
    return (
      <Fragment>
        <div className="gemini-web">
          <div className="bottom-menu-gemini-container">
            <div className="bottom-menu-container">
              <div className="bottom-menu-gemini">
                {
                  selectedRatio
                  && (
                    <GoodwillQuantilsLegend
                      impactedStores={selectedRatio.impactedStores}
                      quartils={selectedRatio.quartilsRatio}
                    />
                  )
                }
              </div>
              <VisioGlobeUI
                className={`${this.state.legendHidden ? 'hidden' : ''}`}
                mapviewer={this.props.mapviewer}
              />
            </div>
          </div>
        </div>
        <div className="mobile-legend-container">
          <div className="mobile-legend">
            <IconButton
              icon={`${!this.state.legendHidden ? 'fas fa-times' : 'fas fa-ellipsis-h'}`}
              className="expand-button legend-button"
              onClick={() => this.hideLegend()}
            />
            {
              selectedRatio
              && (
                <GoodwillQuantilsLegend
                  impactedStores={selectedRatio.impactedStores}
                  quartils={selectedRatio.quartilsRatio}
                />
              )
            }
          </div>
          <VisioGlobeUI
            className={`${this.state.legendHidden ? 'hidden' : ''}`}
            mapviewer={this.props.mapviewer}
          />
        </div>
      </Fragment>
    );
  }
}

export default withRouter(connect(state => ({
  branchLevels: state.shoppingCenterFilters.branchLevelList,
  isCurrencyBySurface: state.store.isCurrencyBySurface,
}))(GoodwillOverlay));
